import type { ModuleManifest, PluginManifest, TreeEntityDefinition, DagEntityDefinition } from '@gzl10/nexus-sdk'
import { getPluginState } from '../../core/plugin-ops.js'
import { prefixPluginTables } from './table-prefix.js'

/** Options for registerPlugin */
export interface RegisterPluginOptions {
  /** Disable endpoint mounting (services still available via init) */
  disableEndpoints?: boolean
}
import { moduleStore, type ModuleSource, type RegisteredModule } from './module-store.js'
import { generateId } from '../../core/utils/id.js'
import { getTableAndSubject } from './definition-extractors.js'

/**
 * Validates uniqueness of tables and subjects when registering a module
 */
function validateUniqueness(mod: ModuleManifest): void {
  const errors: string[] = []

  // Validar label requerido
  if (!mod.label) {
    throw new Error(`Módulo '${mod.name}': label es requerido`)
  }

  for (const def of mod.definitions ?? []) {
    const { table, subject } = getTableAndSubject(def)

    if (table && moduleStore.tables.has(table)) {
      errors.push(`tabla '${table}' ya está registrada`)
    }
    if (subject && moduleStore.subjects.has(subject)) {
      errors.push(`subject '${subject}' ya está registrado`)
    }

    // Validate tree entity seed has at least one root node (only for inline seeds)
    if (def.type === 'tree') {
      const treeDef = def as TreeEntityDefinition
      if (!treeDef.seed) {
        errors.push(`tree entity '${table}' requires seed data`)
      } else if (Array.isArray(treeDef.seed)) {
        // Only validate inline arrays, not URL-based seeds
        if (treeDef.seed.length === 0) {
          errors.push(`tree entity '${table}' requires at least one seed record`)
        } else {
          const hasRoot = treeDef.seed.some((s: Record<string, unknown>) => s['parent_id'] === null || s['parent_id'] === undefined)
          if (!hasRoot) {
            errors.push(`tree entity '${table}' seed must include at least one root node (parent_id: null)`)
          }
        }
      }
      // SeedConfig (URL-based) seeds are validated at runtime during seeding
    }

    // Validate DAG entity seed has at least one record (only for inline seeds)
    if (def.type === 'dag') {
      const dagDef = def as DagEntityDefinition
      if (!dagDef.seed) {
        errors.push(`dag entity '${table}' requires seed data`)
      } else if (Array.isArray(dagDef.seed) && dagDef.seed.length === 0) {
        errors.push(`dag entity '${table}' requires at least one seed record`)
      }
      // Note: DAG roots are nodes without entries in the parents table,
      // which is managed separately from the seed data
    }
  }

  if (errors.length > 0) {
    throw new Error(`Módulo '${mod.name}' tiene conflictos:\n  - ${errors.join('\n  - ')}`)
  }
}

/**
 * Registers tables and subjects for a module
 */
function registerTablesAndSubjects(mod: ModuleManifest): void {
  for (const def of mod.definitions ?? []) {
    const { table, subject } = getTableAndSubject(def)
    if (table) moduleStore.tables.add(table)
    if (subject) moduleStore.subjects.add(subject)
    if (table && subject) moduleStore.tableToSubject.set(table, subject)
  }
}

/**
 * Options for registering a module
 */
interface RegisterModuleOptions {
  /** Module source ('core', 'plugin', 'standalone') */
  source?: ModuleSource
  /** Plugin name (only if source === 'plugin') */
  pluginName?: string
  /** If true, module routes won't be mounted (services still available via init) */
  disableEndpoints?: boolean
  /** Table name prefix for plugin modules */
  tablePrefix?: string
  /** Original (unprefixed) table names declared by this plugin */
  pluginTables?: Set<string>
}

/**
 * Registers a module manifest in the engine store.
 *
 * @remarks
 * Registration is separate from initialization to support dependency ordering.
 * All modules must be registered before the server starts so the engine can
 * resolve dependencies and determine initialization order via topological sort.
 *
 * The engine validates uniqueness of table names and CASL subjects to prevent
 * conflicts between modules. Tree entities require at least one root node in seed.
 *
 * @param mod - Module manifest with name, definitions, and optional init/destroy
 * @param options - Source (core/plugin/standalone) and plugin name if applicable
 *
 * @example
 * ```typescript
 * registerModule(postsModule)
 * registerModule(commentsModule, { source: 'plugin', pluginName: 'cms' })
 * ```
 */
export function registerModule(
  mod: ModuleManifest,
  options: RegisterModuleOptions = {}
): void {
  const { source = 'core', pluginName, disableEndpoints, tablePrefix, pluginTables } = options

  validateUniqueness(mod)
  registerTablesAndSubjects(mod)

  // Generate unique ID and stamp module name for each entity definition
  for (const def of mod.definitions ?? []) {
    (def as unknown as { _id: string })._id = generateId()
    ;(def as unknown as { _moduleName: string })._moduleName = mod.name
  }

  // Generate unique ID for each page definition
  for (const page of mod.pages ?? []) {
    (page as unknown as { _id: string })._id = generateId()
  }

  const registered: RegisteredModule = {
    ...mod,
    _source: source,
    _pluginName: pluginName,
    _disableEndpoints: disableEndpoints,
    _tablePrefix: tablePrefix,
    _pluginTables: pluginTables
  }
  moduleStore.modules.push(registered)
}

/**
 * Check if a plugin should be loaded based on nexus.plugins.json state.
 *
 * @remarks
 * Reads the file-based plugin registry directly (no DB, no module imports).
 *
 * @param pluginName - Name of the plugin to check
 * @param projectPath - Optional project root path
 * @returns true if plugin should be loaded, false if disabled
 */
export function shouldLoadPlugin(pluginName: string, projectPath?: string): boolean {
  const state = getPluginState(pluginName, projectPath)
  // Only load if explicitly enabled in nexus.plugins.json
  if (!state) return false
  return state.enabled
}

/**
 * Registers all modules from a plugin manifest.
 *
 * @remarks
 * Plugins bundle multiple related modules under a single namespace. This enables:
 * - Shared branding (label, icon) inherited by modules without their own
 * - Unified category assignment for UI grouping
 * - Plugin-level lookup via getPlugin() for configuration access
 *
 * Use `disableEndpoints` when you need plugin services available via ctx.services
 * but don't want HTTP routes mounted (e.g., internal-only modules).
 *
 * @param plugin - Plugin manifest with modules array and shared metadata
 * @param options - Optional settings like disableEndpoints
 *
 * @example
 * ```typescript
 * registerPlugin(cmsPlugin)
 * registerPlugin(analyticsPlugin, { disableEndpoints: true })
 * ```
 */
export function registerPlugin(plugin: PluginManifest, options?: RegisterPluginOptions): void {
  // Validate code format: exactly 3 lowercase letters
  if (!/^[a-z]{3}$/.test(plugin.code)) {
    throw new Error(
      `Plugin '${plugin.name}': code must be exactly 3 lowercase letters, got '${plugin.code}'`
    )
  }
  // Validate code uniqueness
  const existingByCode = moduleStore.pluginsByCode.get(plugin.code)
  if (existingByCode) {
    throw new Error(
      `Plugin code '${plugin.code}' already registered by '${existingByCode.name}'`
    )
  }

  // Register in both indexes
  moduleStore.plugins.set(plugin.name, plugin)
  moduleStore.pluginsByCode.set(plugin.code, plugin)

  // Auto-prefix table names with plugin code
  const pluginTables = prefixPluginTables(plugin.code, plugin.modules)
  const tablePrefix = `${plugin.code}_`

  for (const mod of plugin.modules) {
    // Inyectar propiedades del plugin al módulo (solo si no están definidas)
    const moduleWithPluginProps = {
      ...mod,
      label: mod.label ?? plugin.label,
      icon: mod.icon ?? plugin.icon,
      category: plugin.category,
    }
    registerModule(moduleWithPluginProps, {
      source: 'plugin',
      pluginName: plugin.name,
      disableEndpoints: options?.disableEndpoints,
      tablePrefix,
      pluginTables
    })
  }
}
